import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { checkAccess } from "../../../Common/common";
import {
  Button,
  ConfirmDialog,
  ExportExcel,
  employeeID,
  companyID,
} from "../../../Common/UIComponents";
import {
  setFormFields,
  setIsOptionsRequred,
  setPrevFormFields,
  setSelectedFieldDetail,
  fetchFieldTypeList,
  fetchShiftChangeOverColumnList,
  saveShiftChangeOverColumns,
  deleteShiftChangeOverColumn,
} from "../../../API_Service/Settings_Slice";
import Toast from "../../../Common/Toastify";

// See kendo_common.js for why this local binding exists (ESLint no-undef).
const $ = window.$ || window.jQuery;


const FIELD_CATEGORIES = {
  basic: { label: "Basic Inputs", accent: "#08757C", soft: "#e4f2f2" },
  choice: { label: "Choice", accent: "#0a6fa8", soft: "rgba(10,111,168,.12)" },
  date: { label: "Date & Lookup", accent: "#a8620a", soft: "rgba(168,98,10,.12)" },
};

const FIELD_META = {
  Text: { label: "Text", icon: "short_text", cat: "basic" },
  Textarea: { label: "Text Area", icon: "notes", cat: "basic" },
  FileUpload: { label: "File Upload", icon: "upload_file", cat: "basic" },
  CheckBox: { label: "Check Box", icon: "check_box", cat: "choice" },
  Radio: { label: "Radio Button", icon: "radio_button_checked", cat: "choice" },
  Dropdown: { label: "Drop Down", icon: "arrow_drop_down_circle", cat: "choice" },
  MultiDropdown: { label: "Multi Drop Down", icon: "checklist", cat: "choice" },
  DatePicker: { label: "Date Picker", icon: "event", cat: "date" },
  AutoCompleteDropdown: {
    label: "Auto Complete Dropdown",
    icon: "manage_search",
    cat: "date",
  },
};
const fieldMetaFor = (type) =>
  FIELD_META[type] ?? { label: type ?? "Unknown", icon: "help", cat: "basic" };

const OPTION_TYPES = [
  "Dropdown",
  "MultiDropdown",
  "CheckBox",
  "Radio",
  "AutoCompleteDropdown",
];


const PROTECTED_FIELD_LABELS = ["Alternate Project"];
const isProtectedField = (field) =>
  PROTECTED_FIELD_LABELS.some(
    (name) => name.toLowerCase() === field?.label?.trim().toLowerCase(),
  );


const FALLBACK_FIELD_TYPES = Object.keys(FIELD_META).map((type, i) => ({
  FieldTypeID: i + 1,
  FieldType: type,
  FieldTypeDisplayName: FIELD_META[type].label,
}));

const FormBuilder = ({ toggleFieldModal }) => {
  const dispatch = useDispatch();
  const [fieldTypes, setFieldTypes] = useState(null);

  const [saved, setSaved] = useState(false);
  const formFields = useSelector((state) => state?.settings?.formFields);
  const formFieldsRef = useRef();

  useEffect(() => {
    formFieldsRef.current = formFields;
  }, [formFields]);

  useEffect(() => {
    const $palette = $(".draggable");
    $palette.each(function () {
      $(this).data("kendoDraggable")?.destroy();
    });
    $palette.kendoDraggable({
      hint: function (e) {
        return $(
          "<div class='k-card k-shadow-sm' style='padding: 5px;'>" +
            $(e).text() +
            "</div>"
        );
      },
      dragstart: function (e) {
        $(e.currentTarget).data("type", $(e.currentTarget).attr("data-type"));
      },
    });

    // Initialize kendoDropTarget
    const $zone = $("#dropzone");
    $zone.data("kendoDropTarget")?.destroy();
    $zone.kendoDropTarget({
      drop: function (e) {
        addField(
          $(e.draggable.currentTarget).data("type"),
          $(e.draggable.currentTarget).data("id")
        );
      },
    });
  }, [fieldTypes]);

  useEffect(() => {
    if (!formFields) return;

    
    const $zone = $("#dropzone");
    $zone.data("kendoSortable")?.destroy();

    $zone.kendoSortable({
      filter: ".form-element",
      cursor: "move",
      placeholder: function (element) {
        return element.clone().css("opacity", 0.2);
      },
      change: function (e) {
        let updatedFields = [...formFieldsRef.current]; // Use latest formFields
        let movedItem = updatedFields.splice(e.oldIndex, 1)[0];
        updatedFields.splice(e.newIndex, 0, movedItem);
        dispatch(setFormFields(updatedFields));
        dispatch(setPrevFormFields(updatedFields));
        if ($(".form-element")) $(".form-element").css("display", "flex");
      },
    });

    return () => {
      $zone.data("kendoSortable")?.destroy();
    };
  }, [formFields]);

  const openEditModal = (e, id) => {
    let field = formFields?.find((f) => f.id === id);
    dispatch(setSelectedFieldDetail(field));
    if (!field) return;
    dispatch(setIsOptionsRequred(OPTION_TYPES.includes(field.typeName)));
    toggleFieldModal();
  };

  const addField = (type, id) => {
    if (!type) return;
    let value = `New ${type}`;
    let newID = -(formFieldsRef?.current?.length + 1);
    let newField = {
      id: newID,
      typeId: id,
      name: value.trim().replace(/\s+/g, ""),
      typeName: type,
      label: `New ${type}`,
      required: false,
    };

    dispatch(setFormFields([...formFieldsRef?.current, newField]));
    dispatch(setPrevFormFields([...formFieldsRef?.current, newField]));
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = useCallback(
    (e, event) => {
      if (!e || !(formFields?.length > 0)) return;
      event.preventDefault();

      const payload = {
        CompanyID: companyID,
        CreatedBy: employeeID,
        Columns: formFields.map((field) => ({
          Id: field?.id > 0 ? field.id : 0,
          Value: field?.label,
          FieldTypeID: field?.typeId,
          IsRequired: !!field?.required,
          // Only Dropdown/MultiDropdown/CheckBox/Radio/AutoComplete fields
          // carry an options string — everything else sends null and the
          // backend just skips the options-table write for that row.
          Options: OPTION_TYPES.includes(field?.typeName) ? field?.options || null : null,
        })),
      };

      setSaving(true);
      dispatch(saveShiftChangeOverColumns(payload))
        .then((res) => {
          if (res?.payload?.data?.success) {
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
            loadSavedColumns(); // pick up real IDs for anything that was newly inserted
          } else {
            Toast(
              res?.payload?.data?.message || "Failed to save. Please try again.",
              "error",
            );
          }
        })
        .catch((err) => {
          console.log(err);
          Toast("Failed to save. Please try again.", "error");
        })
        .finally(() => setSaving(false));
    },
    [formFields] // eslint-disable-line react-hooks/exhaustive-deps
  );

  
  const excelHeaders = ["S.No", "Field Label", "Field Type", "Required", "Options"];
  const excelData = (formFields ?? []).map((field, i) => [
    i + 1,
    field?.label ?? "",
    fieldMetaFor(field?.typeName).label,
    field?.required ? "Yes" : "No",
    field?.options ?? "",
  ]);

  useEffect(() => {
    FormFieldsTypeList();
    loadSavedColumns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const FormFieldsTypeList = () => {
    dispatch(fetchFieldTypeList())
      .then((res) => {
        const rows = res?.payload?.data;
        if (res?.payload?.success && Array.isArray(rows) && rows.length > 0) {
          setFieldTypes(
            rows.map((t) => ({
              FieldTypeID: t.fieldTypeID,
              FieldType: t.fieldType,
              FieldTypeDisplayName: t.fieldTypeDisplayName,
            })),
          );
        } else {
          setFieldTypes(FALLBACK_FIELD_TYPES);
        }
      })
      .catch((err) => {
        console.log(err);
        setFieldTypes(FALLBACK_FIELD_TYPES);
      });
  };


  const loadSavedColumns = () => {
    dispatch(fetchShiftChangeOverColumnList({ companyID }))
      .then((res) => {
        const rows = res?.payload?.data;
        if (res?.payload?.success && Array.isArray(rows)) {
          dispatch(setFormFields(rows));
          dispatch(setPrevFormFields(rows));
        }
      })
      .catch((err) => console.log(err));
  };


  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const requestDeleteField = (id) => setDeleteDialog({ open: true, id });
  const cancelDeleteField = () => setDeleteDialog({ open: false, id: null });
  const removeFieldLocally = (id) => {
    const updated = formFields.filter((field) => field.id !== id);
    dispatch(setFormFields(updated));
    dispatch(setPrevFormFields(updated));
  };

  const confirmDeleteField = () => {
    const { id } = deleteDialog;


    if (!(id > 0)) {
      removeFieldLocally(id);
      setDeleteDialog({ open: false, id: null });
      Toast("Removed", "success");
      return;
    }

    dispatch(deleteShiftChangeOverColumn({ id, companyID, modifiedBy: employeeID }))
      .then((res) => {
        if (res?.payload?.data?.success) {
          removeFieldLocally(id);
          Toast("Deleted Successfully", "success");
        } else {
          Toast(
            res?.payload?.data?.message || "Failed to delete. Please try again.",
            "error",
          );
        }
      })
      .catch((err) => {
        console.log(err);
        Toast("Failed to delete. Please try again.", "error");
      })
      .finally(() => setDeleteDialog({ open: false, id: null }));
  };

  return (
    <>
        <form id="piFormBuilder-Form">
          <div>
            <div
              className="container flex flex-col lg:flex-row gap-3 lg:gap-4 p-4 sm:p-4"
            >
              <div
                className="left-panel w-full lg:w-[70%] lg:flex-shrink-0"
                style={{
                  padding: "16px",
                  background: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 1px 2px rgba(15,32,33,.04), 0 12px 32px -12px rgba(15,32,33,.12)",
                  border: "1px solid #e5e9e9",
                  minHeight: "400px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "13px",
                      fontWeight: 800,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "#08757C",
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: "17px" }}>
                      dashboard_customize
                    </span>
                    Form Preview
                  </h3>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {[
                      { icon: "list_alt", value: formFields?.length ?? 0, suffix: "fields" },
                      {
                        icon: "priority_high",
                        value: formFields?.filter((f) => f?.required).length ?? 0,
                        suffix: "required",
                      },
                    ].map((stat) => (
                      <div
                        key={stat.suffix}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "#f7fafa",
                          border: "1px solid #e5e9e9",
                          borderRadius: "10px",
                          padding: "5px 10px",
                          fontSize: "11.5px",
                          fontWeight: 700,
                          color: "#55696b",
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: "14px", color: "#08757C" }}
                        >
                          {stat.icon}
                        </span>
                        <span style={{ color: "#181c1d", fontWeight: 800 }}>{stat.value}</span>
                        {stat.suffix}
                      </div>
                    ))}
                  </div>
                </div>
                <div
                  id="dropzone"
                  className="form-builder"
                  style={{
                    minHeight: "400px",
                    border: "2px dashed #d7e2e2",
                    borderRadius: "16px",
                    padding: "12px",
                    background: "#fafcfc",
                    overflowY: "auto",
                    maxHeight: "350px",
                  }}
                >
                  {formFields?.length === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "#9eabac",
                        padding: "44px 20px",
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: "30px", marginBottom: "8px", opacity: 0.7 }}>
                        drag_indicator
                      </span>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 600 }}>
                        Drag a field type here to get started
                      </p>
                    </div>
                  ) : (
                    formFields?.map((field) => {
                      const meta = fieldMetaFor(field?.typeName);
                      const cat = FIELD_CATEGORIES[meta.cat] ?? FIELD_CATEGORIES.basic;
                      const optCount = field?.options
                        ? field.options.split(",").filter(Boolean).length
                        : null;
                      const protectedField = isProtectedField(field);

                      return (
                        <div
                          key={field?.id}
                          className="form-element group"
                          style={{
                            padding: "10px 12px",
                            marginBottom: "8px",
                            background: "#fff",
                            border: "1px solid #ebeeee",
                            borderLeft: `3px solid ${cat.accent}`,
                            borderRadius: "0 12px 12px 0",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            justifyContent: "space-between",
                            cursor: "move",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                            <span
                              className="material-symbols-outlined"
                              style={{ color: "#bdc9c9", fontSize: "18px", flexShrink: 0 }}
                            >
                              drag_indicator
                            </span>
                            <span
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background: cat.soft,
                                color: cat.accent,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                                {meta.icon}
                              </span>
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <strong style={{ fontSize: "14px", color: "#181c1d" }}>
                                {field?.label}
                                {field?.required && (
                                  <span style={{ color: "#e5566a", marginLeft: "4px" }}>*</span>
                                )}
                              </strong>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  marginTop: "3px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 800,
                                    letterSpacing: "0.03em",
                                    padding: "1.5px 7px",
                                    borderRadius: "999px",
                                    background: cat.soft,
                                    color: cat.accent,
                                  }}
                                >
                                  {meta.label}
                                </span>
                                {optCount != null && (
                                  <span style={{ fontSize: "10.5px", color: "#9eabac", fontWeight: 600 }}>
                                    {optCount} option{optCount !== 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {!protectedField && (
                            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                              {checkAccess("APPCONEDIT") && (
                                <button
                                  type="button"
                                  onClick={(e) => openEditModal(e, field?.id)}
                                  title="Edit field"
                                  style={{
                                    border: "none",
                                    background: "none",
                                    color: "#3e494a",
                                    cursor: "pointer",
                                    width: "30px",
                                    height: "30px",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span className="material-symbols-outlined" style={{ fontSize: "17px" }}>
                                    edit
                                  </span>
                                </button>
                              )}
                              {checkAccess("APPCONDELETE") && (
                                <button
                                  type="button"
                                  onClick={() => requestDeleteField(field?.id)}
                                  title="Delete field"
                                  style={{
                                    border: "none",
                                    background: "none",
                                    color: "#3e494a",
                                    cursor: "pointer",
                                    width: "30px",
                                    height: "30px",
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span className="material-symbols-outlined" style={{ fontSize: "17px" }}>
                                    delete
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                <div
                  className="modal-footer mt-2"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "10px",
                    marginTop: "16px",
                    paddingTop: "14px",
                    borderTop: "1px solid #e5e9e9",
                  }}
                >
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {checkAccess("APPCONCREATE") && (
                      <Button
                        variant="primary"
                        onClick={(event) => handleSubmit(true, event)}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save"}
                      </Button>
                    )}
                    <ExportExcel
                      headers={excelHeaders}
                      data={excelData}
                      filename="ShiftChangeOverForm"
                      buttonLabel="Download Excel"
                    />
                  </div>
                  <span style={{ fontSize: "11px", color: "#9eabac", fontWeight: 600 }}>
                    Draft only — <strong style={{ color: "#55696b" }}>nothing saved</strong> until you hit Save
                  </span>
                </div>
              </div>
              {/* RIGHT PANEL - FORM FIELD TYPES */}
              <div
                className="right-panel w-full lg:w-[30%]"
                style={{
                  padding: "16px",
                  background: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 1px 2px rgba(15,32,33,.04), 0 12px 32px -12px rgba(15,32,33,.12)",
                  border: "1px solid #e5e9e9",
                }}
              >
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#08757C",
                    marginBottom: "4px",
                  }}
                >
                  Form Fields
                </h3>
                <p style={{ fontSize: "11px", color: "#9eabac", marginBottom: "12px" }}>
                  Drag onto the canvas, or click to append.
                </p>
                <div id="fieldTypes">
                  {fieldTypes &&
                    Object.keys(FIELD_CATEGORIES).map((catKey) => {
                      const cat = FIELD_CATEGORIES[catKey];
                      const items = fieldTypes.filter(
                        (t) => fieldMetaFor(t?.FieldType).cat === catKey,
                      );
                      if (items.length === 0) return null;
                      return (
                        <div key={catKey} style={{ marginBottom: "12px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              fontSize: "10px",
                              fontWeight: 800,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              color: "#9eabac",
                              marginBottom: "8px",
                              padding: "0 2px",
                            }}
                          >
                            <span>{cat.label}</span>
                            <span>{items.length}</span>
                          </div>
                          {items.map((type) => (
                            <div
                              key={type?.FieldTypeID}
                              className="draggable sco-type-chip"
                              data-id={type?.FieldTypeID}
                              data-type={type?.FieldType}
                              onClick={() => addField(type?.FieldType, type?.FieldTypeID)}
                              style={{
                                padding: "8px 10px",
                                background: "#f7fafa",
                                border: "1px solid #e5e9e9",
                                color: "#181c1d",
                                fontWeight: 700,
                                fontSize: "12.5px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                cursor: "grab",
                                borderRadius: "12px",
                                marginBottom: "5px",
                              }}
                            >
                              <span
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "8px",
                                  background: cat.soft,
                                  color: cat.accent,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: "15px" }}>
                                  {fieldMetaFor(type?.FieldType).icon}
                                </span>
                              </span>
                              {type?.FieldTypeDisplayName}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                </div>
              </div>
              <style>{`
                .sco-type-chip:hover { border-color: #08757C !important; background: #fff !important; }
                .sco-type-chip:active { transform: scale(0.98); }

                /* Kendo's own theme CSS applies rules to sortable list items
                   with !important, which beats a plain inline style — that's
                   what was pushing the edit/delete buttons onto their own
                   line instead of the row's right side. Forced back with
                   matching !important, scoped to this canvas only. */
                #dropzone .form-element {
                  display: flex !important;
                  flex-direction: row !important;
                  flex-wrap: nowrap !important;
                  align-items: center !important;
                  justify-content: space-between !important;
                }
                #dropzone .form-element > div {
                  display: flex !important;
                  flex-direction: row !important;
                  align-items: center !important;
                }
              `}</style>
            </div>
          </div>
        </form>

      <ConfirmDialog
        isOpen={deleteDialog.open}
        variant="danger"
        title="Remove this field?"
        message={
          <p className="text-sm text-[#181c1d]">
            This field will be removed from the form layout. You can't revert
            this.
          </p>
        }
        confirmLabel="Yes, Remove"
        onConfirm={confirmDeleteField}
        onCancel={cancelDeleteField}
      />

      {saved && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-2 bg-[#bbf46e] text-[#112000] px-6 py-3 rounded-xl shadow-lg text-sm font-semibold pointer-events-none"
          role="status"
          style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
        >
          <span
            className="material-symbols-outlined text-[20px]"
            style={{ fontVariationSettings: "'FILL' 1" }}
          >
            check_circle
          </span>
          Shift Change Over saved successfully.
          <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
        </div>
      )}
    </>
  );
};
export default memo(FormBuilder);
